import type { InventoryStoreState } from '@/types/Store'
import { notify } from '@kyvg/vue3-notification'
import { defineStore } from 'pinia'
import { INVENTORY_ROWS_PER_PAGE } from '@/const/Inventory'
import { catchErrors } from '@/services/Helpers'
import InventoryProductsProxy from '@/proxies/Inventory/InventoryProductsProxy'
import InventoryTransactionsProxy from '@/proxies/Inventory/InventoryTransactionsProxy'
import InventoryInboundReceiptsProxy from '@/proxies/Inventory/InventoryInboundReceiptsProxy'

type CreateInventoryProductInput = {
  sku: string
  name: string
}

type CreateInboundReceiptLineInput = {
  sku: string
  qty: number
}

type CreateInboundReceiptInput = {
  description?: string
  lines: CreateInboundReceiptLineInput[]
}

export const useInventoryStore = defineStore('inventory', {
  state: (): InventoryStoreState => ({
    products: [],
    inboundReceipts: [],
    inventoryTransactions: [],
    productsCount: 0,
    inboundReceiptsCount: 0,
    transactionsCount: 0,
    productsLoading: false,
    inboundReceiptsLoading: false,
    transactionsLoading: false,
    productsRequestId: 0,
    inboundReceiptsRequestId: 0,
    transactionsRequestId: 0,
  }),
  getters: {
    productsList: (state) => state.products,
    receiptsList: (state) => state.inboundReceipts,
    transactionsList: (state) => state.inventoryTransactions,
  },
  actions: {
    resetState() {
      this.products = []
      this.inboundReceipts = []
      this.inventoryTransactions = []
      this.productsCount = 0
      this.inboundReceiptsCount = 0
      this.transactionsCount = 0
    },
    async createProduct(companyId: string, input: CreateInventoryProductInput) {
      const response = await new InventoryProductsProxy().create({
        companyId,
        sku: input.sku.trim(),
        name: input.name.trim(),
      })
      const product = response.data
      this.products = [product, ...this.products]
      return product
    },
    async createInboundReceipt(companyId: string, input: CreateInboundReceiptInput) {
      try {
        const response = await new InventoryInboundReceiptsProxy().create({
          companyId,
          description: input.description?.trim() || null,
          lines: input.lines.map((line) => ({
            sku: line.sku.trim(),
            qty: line.qty,
          })),
        })

        const receipt = response.data
        this.inboundReceipts = [receipt, ...this.inboundReceipts]

        await this.fetchTransactions(companyId)
        return receipt
      } catch (error: any) {
        notify({ group: 'error', text: error?.response?.data?.message || 'Failed to create inbound receipt' })
        return []
      }
    },
    async fetchProducts(companyId: string, options: { page: number; rowsPerPage: number }) {
      this.productsLoading = true
      this.productsRequestId += 1
      const requestId = this.productsRequestId
      try {
        const response = await new InventoryProductsProxy({
          companyId,
          perPage: options.rowsPerPage,
          page: options.page,
        }).all()
        if (requestId !== this.productsRequestId) return
        this.products = response.data || []
        this.productsCount = response.count || 0
      } catch (error) {
        if (requestId !== this.productsRequestId) return
        catchErrors(error as any)
        this.products = []
        this.productsCount = 0
      } finally {
        if (requestId === this.productsRequestId) {
          this.productsLoading = false
        }
      }
    },
    async fetchInboundReceipts(companyId: string, options: { page: number; rowsPerPage: number }) {
      this.inboundReceiptsLoading = true
      this.inboundReceiptsRequestId += 1
      const requestId = this.inboundReceiptsRequestId
      try {
        const response = await new InventoryInboundReceiptsProxy({
          companyId,
          perPage: options.rowsPerPage,
          page: options.page,
        }).all()
        if (requestId !== this.inboundReceiptsRequestId) return
        this.inboundReceipts = response.data || []
        this.inboundReceiptsCount = response.count || 0
      } catch (error) {
        if (requestId !== this.inboundReceiptsRequestId) return
        catchErrors(error as any)
        this.inboundReceipts = []
        this.inboundReceiptsCount = 0
      } finally {
        if (requestId === this.inboundReceiptsRequestId) {
          this.inboundReceiptsLoading = false
        }
      }
    },
    async fetchTransactions(
      companyId: string,
      options: { page: number; rowsPerPage: number } = { page: 1, rowsPerPage: INVENTORY_ROWS_PER_PAGE },
    ) {
      this.transactionsLoading = true
      this.transactionsRequestId += 1
      const requestId = this.transactionsRequestId
      try {
        const response = await new InventoryTransactionsProxy({
          companyId,
          perPage: options.rowsPerPage,
          page: options.page,
        }).all()
        if (requestId !== this.transactionsRequestId) return this.inventoryTransactions
        this.inventoryTransactions = response.data || []
        this.transactionsCount = response.count || 0
        return this.inventoryTransactions
      } catch (error) {
        if (requestId !== this.transactionsRequestId) return this.inventoryTransactions
        catchErrors(error as any)
        this.inventoryTransactions = []
        this.transactionsCount = 0
        return this.inventoryTransactions
      } finally {
        if (requestId === this.transactionsRequestId) {
          this.transactionsLoading = false
        }
      }
    },
  },
})
